<template>
  <v-sheet outlined>
    <v-container
      fluid
      class="text-body-2"
    >
      <p class="text-h6">Additional Information</p>

      <!-- Remarks & programs -->
      <v-row>
        <v-col cols="6">
          <div class="overline text-weight-bold">Remarks</div>
          <span v-if="sanction.remarks">
            {{ sanction.remarks.replace(/[()]/g, "") }}
          </span>
          <span v-else>N/A</span>
        </v-col>
        <v-col cols="4">
          <div class="overline text-weight-bold">Programs & Additional Sanctions</div>

          <template v-if="sanction.programs?.length > 0 || sanction.additionalSanctions?.length > 0">
            <span v-if="sanction.programs?.length > 0" class="mr-4">
              {{ sanction.programs.join(", ") }}
            </span>

            <span v-if="sanction.additionalSanctions?.length > 0">
              {{ sanction.additionalSanctions.join(", ") }}
            </span>
          </template>
          <template v-else> N/A </template>
        </v-col>
      </v-row>

      <!-- DOBs, Gender, Citizenship, Birthplace -->
      <template v-if="sanction.type == 'person'">
        <v-row>
          <v-col cols="2">
            <div class="overline text-weight-bold">Birthplace</div>
            <template v-if="sanction.personDetails.birthPlaces && sanction.personDetails.birthPlaces.length > 0">
              {{ sanction.personDetails.birthPlaces.join(", ") }}
            </template>
            <span v-else>N/A</span>
          </v-col>
          <v-col cols="2">
            <div class="overline text-weight-bold">Citizenship</div>
            <span v-if="sanction.personDetails.citizenships && sanction.personDetails.citizenships.length > 0">
              {{ sanction.personDetails.citizenships.join(", ").toUpperCase() }}
            </span>
            <span v-else>N/A</span>
          </v-col>
          <v-col cols="2">
            <div class="overline text-weight-bold">Nationality</div>
            <span v-if="sanction.personDetails.nationalities && sanction.personDetails.nationalities.length > 0">
              {{ sanction.personDetails.nationalities.join(", ") }}
            </span>
            <span v-else>N/A</span>
          </v-col>
          <v-col cols="2">
            <div class="overline text-weight-bold">Date of Birth</div>
            <span v-if="sanction.personDetails.birthDates && sanction.personDetails.birthDates.length > 0">
              {{ sanction.personDetails.birthDates.join(", ") }}
            </span>
            <span v-else>N/A</span>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="2">
            <div class="overline text-weight-bold">Gender</div>
            <span>
              {{ sanction.personDetails?.gender ? sanction.personDetails.gender : "N/A" }}
            </span>
          </v-col>
        </v-row>
      </template>

      <!-- Phone, Email -->
      <v-row>
        <v-col>
          <div class="overline text-weight-bold"><v-icon small>mdi-phone</v-icon> Phone Numbers</div>
          <span v-if="sanction.phoneNumbers && sanction.phoneNumbers.length > 0">
            {{ sanction.phoneNumbers.join(", ") }}
          </span>
          <span v-else>N/A</span>
        </v-col>

        <v-col>
          <div class="overline text-weight-bold"><v-icon small>mdi-email</v-icon> Email</div>
          <span v-if="sanction.emailAddresses && sanction.emailAddresses.length > 0">
            {{ sanction.emailAddresses.join(", ") }}
          </span>
          <span v-else>N/A</span>
        </v-col>
      </v-row>

      <!-- Alias -->
      <v-row>
        <v-col
          cols="12"
          v-if="sanction.alias && sanction.alias.length > 0"
        >
          <div class="overline">Aliases</div>
          <v-chip
            dark
            small
            v-for="(alias, index) in sanction.alias"
            :key="index"
            class="mr-1 mb-1"
            :color="alias.category == 'strong' ? 'red lighten-2' : 'grey lighten-1'"
          >
            {{ alias.name }}
          </v-chip>
        </v-col>
        <v-col v-else> N/A </v-col>
      </v-row>

      <v-row v-if="sanction.personDetails?.positions?.length > 0">
        <v-col cols="12">
          <div class="overline">
            {{ $t("component.resultsPanel.positions") }}
          </div>
          <v-chip
            small
            v-for="(position, index) in sanction.personDetails.positions"
            :key="index"
            class="mr-1 mb-1"
          >
            {{ position }}
          </v-chip>
        </v-col>
      </v-row>

      <!-- Education -->
      <v-row>
        <v-col
          cols="6"
          v-if="sanction.personDetails?.education?.length > 0"
        >
          <div class="overline">
            {{ $t("component.resultsPanel.education") }}
          </div>
          <ul>
            <li
              v-for="(education, index) in sanction.personDetails.education"
              :key="index"
            >
              {{ education }}
            </li>
          </ul>
        </v-col>

        <v-col
          cols="6"
          v-if="sanction.websites?.length > 0"
        >
          <div class="overline">
            {{ $t("component.resultsPanel.websites") }}
          </div>
          <ul>
            <li
              v-for="(website, index) in sanction.websites"
              :key="index"
            >
              {{ website }}
            </li>
          </ul>
        </v-col>
      </v-row>

      <!-- Addresses -->
      <v-row dense class="mb-2">
        <v-col cols="12">
          <div class="overline">Addresses</div>
          <template v-if="!sanction.addresses?.length > 0">N/A</template>
        </v-col>
        <template v-if="sanction.addresses && sanction.addresses.length > 0">
          <template v-for="(address, index) in sanction.addresses">
            <v-col
              cols="3"
              :key="'col-' + index"
            >
              <Address
                :key="address.uid"
                :address1="address.address1"
                :address2="address.address2"
                :city="address.city"
                :state="address.stateOrProvince"
                :postalCode="address.postalCode"
                :country="address.country"
              />
            </v-col>
          </template>
          <v-spacer />
        </template>
      </v-row>

      <!-- IDs -->
      <v-row dense>
        <v-col cols="12">
          <div class="overline">Passports & Other IDs</div>
          <template v-if="!sanction.identifications?.length > 0">N/A</template>
        </v-col>
        <template v-if="sanction.identifications && sanction.identifications.length > 0">
          <template v-for="(identification, index) in sanction.identifications">
            <v-col
              cols="3"
              :key="'col-' + index"
            >
            <v-sheet outlined class="pa-2 fill-height">
              <ID
                :type="identification.type"
                :country="identification.country"
                :number="identification.idNumber"
                icon="fa-id-card"
              />
              </v-sheet>
            </v-col>
          </template>
          <v-spacer />
        </template>
      </v-row>
    </v-container>
  </v-sheet>
</template>

<script>
import Address from "../Address";
import ID from "../../components/ID.vue";
export default {
  name: "TargetData",
  components: {
    Address,
    ID,
  },
  props: {
    sanction: {},
  },
  created() {
    // console.log("sanction: " + JSON.stringify(this.sanction));
  },
  methods: {
    getAkaName(aka) {
      var name = "";
      if (aka.firstName && aka.lastName) {
        name = aka.firstName + " " + aka.lastName;
      } else if (aka.firstName) {
        name = aka.firstName;
      } else if (aka.lastName) {
        name = aka.lastName;
      }
      return name;
    },
  },
};
</script>
